import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import shoorahLogo from '../../../assets/images/shoorah-logo.jpg';
import { getLocalStorageItem } from '../../../utils/helper';

const ChallengesCard = ({
  title,
  onViewAllTap,
  dataList,
  preview = false,
  view = true
}) => {
  const navigate = useNavigate();
  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));  
  const calculateRemainingDays = (endDate) => {
    if (!endDate) return null;
    
    const today = new Date();
    const end = new Date(endDate);
    
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    
    const timeDifference = end.getTime() - today.getTime();
    const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
    
    return daysDifference;
  };

  const getExpiryText = (endDate) => {
    const remainingDays = calculateRemainingDays(endDate);
    
    if (remainingDays === null) return 'No expiry date';
    if (remainingDays < 0) return 'Expired';
    if (remainingDays === 0) return 'Expires today';
    if (remainingDays === 1) return 'Expires in 1 day';
    return `Expires in ${remainingDays} days`;
  };

  const getProgressPercentage = (endDate, startDate = null, totalDuration = 30) => {
    const remainingDays = calculateRemainingDays(endDate);
    
    if (remainingDays === null || remainingDays < 0) return 100;
    
    // If startDate is provided, calculate based on actual duration
    if (startDate && startDate !== null && startDate !== undefined) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const today = new Date();
      
      // Validate dates
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        console.warn('Invalid start or end date provided');
        return 0;
      }
      
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      
      const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 3600 * 24));
      const elapsedDays = Math.ceil((today.getTime() - start.getTime()) / (1000 * 3600 * 24));
      
      // Debug logging - remove after testing
      console.log('Progress calculation:', {
        startDate,
        endDate,
        totalDays,
        elapsedDays,
        remainingDays,
        calculatedPercentage: totalDays > 0 ? Math.round((elapsedDays / totalDays) * 100) : 100
      });
      
      if (totalDays <= 0) return 100;
      
      // Ensure elapsed days is not negative
      const adjustedElapsedDays = Math.max(0, elapsedDays);
      const percentage = Math.min(Math.max((adjustedElapsedDays / totalDays) * 100, 0), 100);
      return Math.round(percentage);
    }
    
    // Fallback: calculate based on remaining days vs total duration
    console.log('Using fallback calculation with totalDuration:', totalDuration, 'remainingDays:', remainingDays);
    const elapsedDays = totalDuration - remainingDays;
    const percentage = Math.min(Math.max((elapsedDays / totalDuration) * 100, 0), 100);
    return Math.round(percentage);
  };

  // Truncate text function
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  return (
    <div className="bg-white border-[1.5px] border-shoorah-borderGray rounded-[16px] mt-5 mb-[18px] px-5 md:px-6 pt-6 pb-8 dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder">
      <div className="horizontal-card-header grid grid-cols-12 mb-[27px] items-center">
        <div className="sm:col-span-7 col-span-9">
          <p className="sm:text-2xl text-[14px] leading-9 font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg">
            {title}
          </p>
        </div>
        {view && (
          <div className="sm:col-span-5 col-span-3">
            <button
              className="sm:text-[14px] text-[12px] text-shoorah-active ms-auto w-full text-right text-nowrap"
              onClick={() => {
                onViewAllTap();
              }}
            >
              View All
            </button>
          </div>
        )}
      </div>

{dataList?.length === 0 ? (
  <div className="flex justify-center items-center h-full w-full">
    <p className="text-[20px] text-shoorah-customGray">No data available</p>
  </div>
) : (
  <div className="grid md:grid-cols-3 grid-cols-1 h-auto gap-4 mt-5 w-full ">
    {(view ? dataList?.slice(0, preview ? 6 : 3) : dataList)?.map((data, index) => (
      <div
        key={index}
        onClick={() => {
          navigate(
            userData?.slug
              ? `/${userData?.slug}/challenges/create-new-challenges?page=details&newchallenge=false`
              : '/challenges/create-new-challenges?page=details&newchallenge=false',
            {
              state: { _id: data?._id },
            }
          );
        }}
        className="cursor-pointer card-content grid grid-cols-12 border-[1.5px] border-shoorah-borderGray rounded-2xl p-[14px] w-full gap-[16px] flex-shrink-0 dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder h-[150px]"
      >
        {/* Left Text Content */}
        <div className="content-box col-span-7 h-full flex flex-col justify-between">
          <div>
            <p className="font-medium leading-5 text-[#000000] dark:text-shoorah-darkBg line-clamp-2 overflow-hidden text-ellipsis">
              {data?.title}
            </p>
            <p className="text-xs text-[#000000] font-normal leading-4 dark:text-shoorah-darkBg line-clamp-2 overflow-hidden text-ellipsis mt-3">
              {data?.description}
            </p>
          </div>
          <div>
            {preview ? (
              <Link
                to={
                  userData?.slug
                    ? `/${userData?.slug}/challenges/create-new-challenges?page=preview&newchallenge=false`
                    : '/challenges/create-new-challenges?page=preview&newchallenge=false'
                }
                state={{ _id: data?._id }}
                onClick={(e) => e.stopPropagation()}
                className="font-medium px-4 py-[6px] bg-[#F3F3F3] text-[#1A2230] rounded-full xl:text-[13px] text-[13px] max-w-[85px]"
              >
                Preview
              </Link>
            ) : (
              <>
                <p className="text-[12px] mb-[0px] text-[#0F0F0F] font-normal leading-4 dark:text-shoorah-darkBg">
                  {getExpiryText(data?.endDate)}
                </p>
                <div className="h-[4px] w-full relative bg-[#E8E8E8] rounded-[4px] mt-1">
                  <div
                    className="absolute top-0 left-0 h-full bg-[#1F1F1F] rounded-[4px]"
                    style={{
                      width: `${getProgressPercentage(data?.endDate, data?.startDate)}%`,
                    }}
                  ></div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Image Box */}
        <div className="img-box col-span-5 rounded-xl w-full h-[120px]">
          <img
            src={data?.image || shoorahLogo}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = shoorahLogo;
            }}
            alt="Template"
            className="w-full h-full object-cover object-center rounded-xl border border-black-300"
          />
        </div>
      </div>
    ))}
  </div>
)}

      
    </div>
  );
};

export default ChallengesCard;